/**
 * Promise wrapper around `<img>.onload` / `onerror`, deduplicating the
 * copies that lived in image-editor's SVG rasterizer and litsquad's avatar
 * editor. Both set `crossOrigin = 'anonymous'` — this package's canvases
 * feed `toBlob`/`getImageData`, and anonymous CORS keeps them untainted
 * when the server cooperates.
 */

/** How much of a failing `src` the error retains — in the message AND on
 *  `.src`. Data URLs can carry megabytes of encoded image; retaining that
 *  anywhere on the error object leaks it into memory, telemetry that
 *  serializes custom error properties, and diagnostic inspection (M5
 *  post-commit review #1). No consumer needs the full value back from the
 *  error — the caller still holds the src it passed in. */
const MAX_SRC_RETAINED = 200;

export class ImageLoadError extends Error {
  /** The source that failed to load, TRUNCATED at 200 chars (see above). */
  readonly src: string;

  constructor(src: string) {
    const retained = src.length > MAX_SRC_RETAINED ? `${src.slice(0, MAX_SRC_RETAINED)}…` : src;
    super(`Failed to load image: ${retained}`);
    this.name = 'ImageLoadError';
    this.src = retained;
  }
}

export interface LoadImageOptions {
  /**
   * Value for `img.crossOrigin`. Default `'anonymous'` (see module header).
   * Pass `null` to leave the property unset — needed for callers loading
   * same-origin-credentialed images where an anonymous CORS request could
   * fail outright.
   */
  crossOrigin?: 'anonymous' | 'use-credentials' | null;
}

/**
 * Load `src` into a fresh `HTMLImageElement`. Resolves with the element on
 * load; rejects with `ImageLoadError` on error. `crossOrigin` is assigned
 * BEFORE `src` — assignment order matters for whether the browser issues a
 * CORS fetch. No `decode()` call: resolution timing matches the `<img>`
 * `onload` behavior every pre-extraction copy had.
 */
export function loadImage(src: string, options?: LoadImageOptions): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    const crossOrigin = options?.crossOrigin === undefined ? 'anonymous' : options.crossOrigin;
    if (crossOrigin !== null) {
      img.crossOrigin = crossOrigin;
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new ImageLoadError(src));
    img.src = src;
  });
}
